import '@patsnap-ui/icon/assets/solid/TickCircle.svg'
import { getLangDegraded } from '@patsnap/synapse_common_utils'
import { useLocale } from '@pharmsnap/shared/composition'
import { EMPTY_PLACEHOLDER } from '@pharmsnap/shared/constants'
import { sharedCtx } from '@pharmsnap/shared/context'
import { TRANSLATIONAL_MEDICINE_LIST_QUERY_KEY } from '@pharmsnap/shared/src/types/translational-medicine'
import { IBaseDrugInfo } from '@pharmsnap/shared/types'
import { computed, defineComponent, PropType, toRefs } from '@vue/composition-api'
import { BDrugPatentMiningTag, BDrugPopperCard, BEntityTag, GIcon, GLink, GTooltip } from '../..'
import $style from './BDrugItem.module.scss'

export const BDrugItem = defineComponent({
  name: 'BDrugItem',
  props: {
    data: {
      type: Object as PropType<IBaseDrugInfo>,
    },
    defaultColor: {
      type: String as PropType<'blue' | 'dark'>,
      default: 'dark',
    },
    showIcon: {
      type: Boolean,
      default: false,
    },
    showCard: {
      type: Boolean,
      default: false,
    },
    // 是否显示双语，英文 然后换行中文
    showBilingual: {
      type: Boolean,
      default: false,
    },
    /**
     * 双语显示的位置
     */
    bilingualPosition: {
      type: String as PropType<'vertical' | 'horizontal'>,
      default: 'vertical',
    },
    /**
     * 显示纯文本，不加链接
     */
    textOnly: {
      type: Boolean,
      default: false,
    },
    zIndex: Number,
    coreIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    displayType: {
      type: String as PropType<'tag' | 'text'>,
      default: 'tag',
    },
    tmIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props, ctx) {
    const { data } = toRefs(props)
    const { isCN, locale } = useLocale()
    const href = computed(() => (data?.value?.drug_id && !props.textOnly ? sharedCtx.router.generatorDrugPath(data.value.drug_id) : ''))
    const displayName = computed(() => getLangDegraded(data?.value, locale.value))
    const name_en = computed(() => (data?.value ? data.value.display_name_en : ''))
    const name_cn = computed(() => (data?.value ? data.value.display_name_cn : ''))
    const patent_mining = computed(() => data?.value?.patent_mining)
    const showCoreIcon = computed(() => props.coreIds.includes(data?.value?.drug_id || ''))
    const isTranslationalMedicine = computed(() => ctx.root.$route?.query?.from === TRANSLATIONAL_MEDICINE_LIST_QUERY_KEY)
    const showTmIcon = computed(() => isTranslationalMedicine.value && props.tmIds.includes(data?.value?.drug_id || ''))
    return {
      href,
      patent_mining,
      displayName,
      name_en,
      name_cn,
      isCN,
      showCoreIcon,
      showTmIcon,
    }
  },
  methods: {
    renderTitle(name: string) {
      const _renderDrugContent = () => {
        const textContent = [
          this.showIcon ? <GIcon class="text-text-t2 mr-1 flex-shrink-0 " svgName="Drug" /> : null,
          <GLink data-selection-disabled={this.showCard} href={this.href} name={name} defaultColor={this.defaultColor}></GLink>,
          this.showCoreIcon ? (
            <GTooltip
              class="mr-[-2px]"
              theme="light"
              mode="icon"
              iconSize={20}
              iconName="SolidTickCircle"
              iconClass="text-blue-default"
              content={this.$tc('common.coreEntity')}
            ></GTooltip>
          ) : null,
        ]
        const contentCls = this.showTmIcon ? $style.highlightTm : ''
        if (this.displayType === 'text') {
          return this.showTmIcon ? <span class={[contentCls, 'max-w-full inline-block']}>{textContent}</span> : textContent
        }
        return this.showTmIcon ? (
          <span class={[contentCls, 'max-w-full inline-block']}>
            <BEntityTag title={name} data-testid="b-drug-item">
              {textContent}
            </BEntityTag>
          </span>
        ) : (
          <BEntityTag title={name} data-testid="b-drug-item">
            {textContent}
          </BEntityTag>
        )
      }

      const id = this.data?.drug_id
      if (id && this.showCard) {
        return (
          <BDrugPopperCard id={id} zIndex={this.zIndex}>
            {_renderDrugContent()}
          </BDrugPopperCard>
        )
      }
      return _renderDrugContent()
    },
  },
  render() {
    if (this.showBilingual) {
      const isHorizontal = this.bilingualPosition === 'horizontal'
      return (
        <div class={isHorizontal ? 'flex items-center gap-1' : 'flex flex-col gap-1'}>
          <div>{this.renderTitle(this.name_en)}</div>
          {this.isCN && this.name_cn && this.name_cn !== this.name_en && <div class="text-text-default text-sm truncate pl-2">{this.name_cn}</div>}
          {this.patent_mining && (
            <div>
              <BDrugPatentMiningTag></BDrugPatentMiningTag>
            </div>
          )}
        </div>
      )
    }
    return (this as any).renderTitle(this.displayName || EMPTY_PLACEHOLDER)
  },
})
